import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  LuUsers, LuCalendarDays, LuMail, LuPhone, LuGlobe, LuTrendingUp,
} from "react-icons/lu";
import { authFetch } from "../../utils/api";
import { ToastProvider, useToast } from "./ui";
import SearchCard from "./SearchCard";
import SearchProgress from "./SearchProgress";
import LeadsTable from "./LeadsTable";
import LeadDrawer from "./LeadDrawer";
import LeadFinderAnalytics from "./LeadFinderAnalytics";
import "./LeadFinder.css";

const ACTIVE_STATUSES = new Set(["queued", "running", "paused"]);

function StatCards({ stats }) {
  const cards = [
    { label: "Total Leads", value: stats?.totalLeads ?? 0, icon: <LuUsers /> },
    { label: "Today's Leads", value: stats?.todaysLeads ?? 0, icon: <LuCalendarDays /> },
    { label: "Emails Found", value: stats?.emailsFound ?? 0, icon: <LuMail /> },
    { label: "Phone Numbers", value: stats?.phonesFound ?? 0, icon: <LuPhone /> },
    { label: "Websites Found", value: stats?.websitesFound ?? 0, icon: <LuGlobe /> },
    { label: "Success Rate", value: `${stats?.successRate ?? 0}%`, icon: <LuTrendingUp /> },
  ];
  return (
    <div className="lf-stats-grid">
      {cards.map((c) => (
        <div className="lf-stat-card" key={c.label}>
          <div className="lf-stat-icon">{c.icon}</div>
          <div className="lf-stat-value">
            {typeof c.value === "number" ? c.value.toLocaleString() : c.value}
          </div>
          <div className="lf-stat-label">{c.label}</div>
        </div>
      ))}
    </div>
  );
}

const LF_TABS = new Set(["map", "insta", "leads", "analytics"]);

function leadFinderTabFromHash() {
  const parts = String(window.location.hash || "").replace(/^#\/?/, "").split("/");
  const tab = (parts[1] || "map").toLowerCase();
  return LF_TABS.has(tab) ? tab : "map";
}

function setLeadFinderHash(tab) {
  const next = `#/lead-finder/${tab}`;
  if (window.location.hash !== next) {
    window.history.replaceState(null, "", next);
  }
}

const SCRAPER_TABS = {
  map: {
    provider: "google_maps",
    label: "Map Scraper",
    title: "Find Leads on Google Maps",
    subtitle: "Discover local businesses by keyword and location — pulls phone, address, website & rating.",
  },
  insta: {
    provider: "instagram",
    label: "Insta Scraper",
    title: "Find Leads on Instagram",
    subtitle: "Discover Instagram businesses & professionals by keyword and location.",
  },
};

function LeadFinderInner() {
  const [tab, setTab] = useState(() => leadFinderTabFromHash());
  const [stats, setStats] = useState(null);
  // Active search tracked per provider so each scraper tab shows its own run.
  const [activeSearches, setActiveSearches] = useState({ google_maps: null, instagram: null });
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const notify = useToast();
  const pollRef = useRef(null);

  const selectTab = (next) => {
    setTab(next);
    setLeadFinderHash(next);
  };

  const loadStats = useCallback(async () => {
    try {
      const data = await authFetch("/lead-finder/stats");
      setStats(data);
    } catch (_) { /* stats are best-effort */ }
  }, []);

  const setActiveForProvider = useCallback((provider, search) => {
    setActiveSearches((prev) => ({ ...prev, [provider]: search }));
  }, []);

  // Adopt the most recent search per provider on mount.
  const loadLatestSearches = useCallback(async () => {
    try {
      const { searches } = await authFetch("/lead-finder/searches?limit=25");
      const latest = {};
      for (const s of searches || []) {
        const p = s.provider || "instagram";
        if (!latest[p]) latest[p] = s;
      }
      setActiveSearches((prev) => ({ ...prev, ...latest }));
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadStats();
    loadLatestSearches();
  }, [loadStats, loadLatestSearches]);

  // Poll any live searches (across both providers) and refresh table + stats.
  useEffect(() => {
    const liveIds = Object.values(activeSearches)
      .filter((s) => s && ACTIVE_STATUSES.has(s.status))
      .map((s) => ({ id: s.id, provider: s.provider }));

    if (liveIds.length === 0) {
      if (pollRef.current) clearInterval(pollRef.current);
      return undefined;
    }

    pollRef.current = setInterval(async () => {
      try {
        const updated = await Promise.all(
          liveIds.map(({ id }) => authFetch(`/lead-finder/search/${id}`).then((r) => r.search).catch(() => null))
        );
        setActiveSearches((prev) => {
          const next = { ...prev };
          for (const s of updated) if (s) next[s.provider || "instagram"] = s;
          return next;
        });
        setRefreshKey((k) => k + 1);
        loadStats();
      } catch (_) { /* transient */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeSearches, loadStats]);

  const startSearch = async (payload) => {
    try {
      const { search } = await authFetch("/lead-finder/search", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setActiveForProvider(payload.provider, search);
      notify("Search started — collecting leads in the background", "success");
    } catch (err) {
      notify(err.message || "Failed to start search", "error");
    }
  };

  const control = async (provider, action) => {
    const active = activeSearches[provider];
    if (!active) return;
    try {
      const { search } = await authFetch(`/lead-finder/search/${action}`, {
        method: "PUT",
        body: JSON.stringify({ searchId: active.id }),
      });
      setActiveForProvider(provider, search);
      notify(`Search ${action}${action.endsWith("e") ? "d" : "ped"}`, "info");
    } catch (err) {
      notify(err.message || `Failed to ${action}`, "error");
    }
  };

  const renderScraper = (key) => {
    const cfg = SCRAPER_TABS[key];
    const active = activeSearches[cfg.provider];
    const isLive = active && ACTIVE_STATUSES.has(active.status);
    return (
      <>
        <SearchCard onStart={startSearch} disabled={isLive} provider={cfg.provider} title={cfg.title} />
        {active && (
          <SearchProgress
            search={active}
            onPause={() => control(cfg.provider, "pause")}
            onResume={() => control(cfg.provider, "resume")}
            onStop={() => control(cfg.provider, "stop")}
          />
        )}
      </>
    );
  };

  const subtitle = SCRAPER_TABS[tab]?.subtitle
    || "Browse, filter and export every lead you've collected.";

  return (
    <div className="lead-finder-page">
      <div className="lf-header">
        <div>
          <h1 className="lf-title">Lead Finder</h1>
          <p className="lf-subtitle">{subtitle}</p>
        </div>
        <div className="lf-tabs">
          <button className={`lf-tab ${tab === "map" ? "active" : ""}`} onClick={() => selectTab("map")}>Map Scraper</button>
          <button className={`lf-tab ${tab === "insta" ? "active" : ""}`} onClick={() => selectTab("insta")}>Insta Scraper</button>
          <button className={`lf-tab ${tab === "leads" ? "active" : ""}`} onClick={() => selectTab("leads")}>Leads</button>
          <button className={`lf-tab ${tab === "analytics" ? "active" : ""}`} onClick={() => selectTab("analytics")}>Analytics</button>
        </div>
      </div>

      <StatCards stats={stats} />

      {tab === "map" && renderScraper("map")}
      {tab === "insta" && renderScraper("insta")}
      {tab === "leads" && <LeadsTable onOpenLead={setDrawerLeadId} refreshKey={refreshKey} />}
      {tab === "analytics" && <LeadFinderAnalytics stats={stats} />}

      {drawerLeadId && (
        <LeadDrawer leadId={drawerLeadId} onClose={() => setDrawerLeadId(null)} />
      )}
    </div>
  );
}

function LeadFinder() {
  return (
    <ToastProvider>
      <LeadFinderInner />
    </ToastProvider>
  );
}

export default LeadFinder;
